import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useState } from 'react';
import { styled } from 'styletron-react';
import { staticColors } from '../../../const/colors/staticColors';
import { sizes } from '../../../const/sizes';
import { numberToPx } from '../../../helpers/rem';
import { useIsInputFocused } from '../../../hooks/useIsInputFocused';
import { useTestIdAttribute } from '../../../hooks/useTestIdAttribute';
import { FlexContainer } from '../../layout/FlexContainer';
import { InputBase } from '../InputBase/InputBase';
import { useNormalInputBaseStyle, useNormalInputWrapperStyle, } from '../useInputStyles';
import { useIsErrorTestAttribute } from '../useIsErrorTestAttribute';
const TEXT_AREA_PADDING = 0;
const StyledTextArea = styled('textarea', (p) => ({
    ...p.$style,
    resize: 'vertical',
    minHeight: sizes.textArea.minHeight,
    padding: TEXT_AREA_PADDING,
    '::placeholder': {
        color: staticColors.inputPlaceholder,
        opacity: 1,
        backgroundColor: p.$overrideBackgroundColor
            ? p.$overrideBackgroundColor
            : p.$style.backgroundColor,
    },
}));
const StyledWrapper = styled('div', (p) => p.$style);
export const TextArea = forwardRef(({ label, caption, disabled, error = false, updated = false, warning = false, readOnly = false, isRequiredLabel = false, gapRight = false, autoResize = false, testId, tooltip, tooltipTabIndex, afterElement, onFocus, onBlur, onInput, min, max, height: initialHeight = sizes.textArea.minHeight, overrideBackgroundColor, ...rest }, ref) => {
    const { isFocused, handleBlur, handleFocus } = useIsInputFocused({
        disabled,
        readOnly,
        onBlur,
        onFocus,
    });
    const isErrorTestAttribute = useIsErrorTestAttribute(!!error);
    const testIdAttribute = useTestIdAttribute(testId);
    const inputStyle = useNormalInputBaseStyle({ readOnly, warning });
    const wrapperStyle = useNormalInputWrapperStyle({
        isFocused,
        readOnly,
        warning,
    });
    const [height, setHeight] = useState(initialHeight);
    const handleResizableInput = (event) => {
        onInput?.(event);
        const isDefaultPrevented = event.isDefaultPrevented();
        if (!isDefaultPrevented) {
            const paddingFromBothSides = 2 * TEXT_AREA_PADDING;
            const heightToSet = event.currentTarget.scrollHeight - paddingFromBothSides;
            setHeight(numberToPx(heightToSet));
        }
    };
    const handleInput = autoResize ? handleResizableInput : onInput;
    const textAreaStyle = {
        ...inputStyle,
        height,
        backgroundColor: overrideBackgroundColor
            ? overrideBackgroundColor
            : inputStyle.backgroundColor,
    };
    return (_jsx(InputBase, { label: label, isRequiredLabel: isRequiredLabel, caption: caption, tooltip: tooltip, tooltipTabIndex: tooltipTabIndex, error: error, updated: updated, warning: warning, isFocused: isFocused, gapRight: gapRight, testId: testId, children: _jsx(StyledWrapper, { $style: wrapperStyle, children: _jsxs(FlexContainer, { horizontalAlign: 'spaceBetween', verticalAlign: 'top', children: [_jsx(StyledTextArea, { ref: ref, $style: textAreaStyle, $overrideBackgroundColor: overrideBackgroundColor, disabled: disabled, readOnly: readOnly, onFocus: handleFocus, onBlur: handleBlur, onInput: handleInput, ...rest, ...testIdAttribute, ...isErrorTestAttribute }), afterElement] }) }) }));
});
